import React, { useState } from "react";
import { MdBusiness, MdArrowForward, MdPerson, MdCalendarToday, MdArchive, MdUnarchive, MdMoreVert, MdPushPin } from "react-icons/md";
import { Company } from "../../utils/api/endpoints/companies";
import moment from "moment";

interface CompanyCardProps {
  company: Company;
  programCount: number;
  latestProgramEndDate?: string | null;
  earliestProgramStartDate?: string | null;
  onClick: () => void;
  onArchiveToggle?: (companyId: number, currentStatus: boolean) => void;
  onPinToggle?: (companyId: number, currentStatus: boolean) => void;
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  programCount,
  latestProgramEndDate,
  earliestProgramStartDate,
  onClick,
  onArchiveToggle,
  onPinToggle
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const handleArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onArchiveToggle) {
      onArchiveToggle(company.company_id, company.is_archived || false);
    }
    setShowMenu(false);
  };

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onPinToggle) {
      onPinToggle(company.company_id, company.is_pinned || false);
    }
    setShowMenu(false);
  };

  // Calculate time remaining or elapsed for end date
  const getTimeInfo = () => {
    if (!latestProgramEndDate) return null;

    const endDate = moment(latestProgramEndDate);
    const now = moment();
    const diffDays = endDate.diff(now, 'days');
    const diffMonths = endDate.diff(now, 'months');
    const diffYears = endDate.diff(now, 'years');

    if (diffDays < 0) {
      // Program has ended
      const absDays = Math.abs(diffDays);
      const absMonths = Math.abs(diffMonths);
      const absYears = Math.abs(diffYears);

      if (absYears > 0) {
        return { text: `${absYears} year${absYears > 1 ? 's' : ''} ago`, isExpired: true };
      } else if (absMonths > 0) {
        return { text: `${absMonths} month${absMonths > 1 ? 's' : ''} ago`, isExpired: true };
      } else {
        return { text: `${absDays} day${absDays > 1 ? 's' : ''} ago`, isExpired: true };
      }
    } else {
      // Program is ongoing or future
      if (diffYears > 0) {
        return { text: `${diffYears} year${diffYears > 1 ? 's' : ''} left`, isExpired: false };
      } else if (diffMonths > 0) {
        return { text: `${diffMonths} month${diffMonths > 1 ? 's' : ''} left`, isExpired: false };
      } else {
        return { text: `${diffDays} day${diffDays > 1 ? 's' : ''} left`, isExpired: false };
      }
    }
  };

  // Calculate time until start date
  const getStartTimeInfo = () => {
    if (!earliestProgramStartDate) return null;

    const startDate = moment(earliestProgramStartDate);
    const now = moment();
    const diffDays = startDate.diff(now, 'days');
    const diffMonths = startDate.diff(now, 'months');
    const diffYears = startDate.diff(now, 'years');

    if (diffDays < 0) {
      // Program has already started
      return { text: 'Started', isUpcoming: false };
    } else {
      // Program is upcoming
      if (diffYears > 0) {
        return { text: `Starts in ${diffYears} year${diffYears > 1 ? 's' : ''}`, isUpcoming: true };
      } else if (diffMonths > 0) {
        return { text: `Starts in ${diffMonths} month${diffMonths > 1 ? 's' : ''}`, isUpcoming: true };
      } else if (diffDays > 0) {
        return { text: `Starts in ${diffDays} day${diffDays > 1 ? 's' : ''}`, isUpcoming: true };
      } else {
        return { text: 'Starts today', isUpcoming: true };
      }
    }
  };

  const timeInfo = getTimeInfo();
  const startTimeInfo = getStartTimeInfo();
  return (
    <div
      onClick={onClick}
      className={`group relative cursor-pointer overflow-hidden rounded-lg border p-6 shadow-sm transition-all duration-200 ${
        company.is_archived
          ? "border-orange-300 bg-orange-50 hover:border-orange-400 hover:shadow-md"
          : "border-gray-200 bg-white hover:border-blue-400 hover:shadow-md"
      }`}
    >
      {/* Archive Menu Button */}
      {onArchiveToggle && (
        <div className="absolute right-4 top-4 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="rounded-full bg-white p-2 shadow-md transition-all hover:bg-gray-50 hover:shadow-lg"
            title="More options"
          >
            <MdMoreVert className="h-5 w-5 text-gray-600" />
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <>
              {/* Backdrop to close menu */}
              <div
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(false);
                }}
              />

              {/* Menu */}
              <div className="absolute right-0 top-12 z-20 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                {onPinToggle && (
                  <button
                    onClick={handlePinClick}
                    className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-gray-50 border-b border-gray-100"
                  >
                    {company.is_pinned ? (
                      <>
                        <MdPushPin className="h-5 w-5 text-gray-600 rotate-45" />
                        <span className="text-gray-700">Unpin</span>
                      </>
                    ) : (
                      <>
                        <MdPushPin className="h-5 w-5 text-blue-600" />
                        <span className="text-blue-700">Pin to Top</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleArchiveClick}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-gray-50"
                >
                  {company.is_archived ? (
                    <>
                      <MdUnarchive className="h-5 w-5 text-green-600" />
                      <span className="text-green-700">Unarchive</span>
                    </>
                  ) : (
                    <>
                      <MdArchive className="h-5 w-5 text-orange-600" />
                      <span className="text-orange-700">Archive</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Pinned Badge */}
      {company.is_pinned && !company.is_archived && (
        <div className={`absolute ${onArchiveToggle ? 'right-16' : 'right-4'} top-4 flex items-center gap-1 rounded-full bg-blue-500 px-2 py-1 text-xs font-semibold text-white`}>
          <MdPushPin className="h-3 w-3" />
          Pinned
        </div>
      )}

      {/* Archived Badge */}
      {company.is_archived && (
        <div className={`absolute ${onArchiveToggle ? 'right-16' : 'right-4'} ${company.is_pinned ? 'top-12' : 'top-4'} flex items-center gap-1 rounded-full bg-orange-500 px-2 py-1 text-xs font-semibold text-white`}>
          <MdArchive className="h-3 w-3" />
          Archived
        </div>
      )}

      {/* Company Logo or Icon */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {company.company_logo ? (
            <img
              src={company.company_logo}
              alt={company.company_name}
              className="h-16 w-16 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
              <MdBusiness className="h-8 w-8 text-white" />
            </div>
          )}
          <div className={onArchiveToggle ? "pr-12" : ""}>
            <h3 className="text-xl font-semibold text-gray-900">
              {company.company_name}
            </h3>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">
                Company ID: {company.company_id}
              </p>
              {company.subscription_type && (
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                  company.subscription_type === "yearly" ? "bg-purple-100 text-purple-800" :
                  company.subscription_type === "half_yearly" ? "bg-blue-100 text-blue-800" :
                  company.subscription_type === "quarterly" ? "bg-green-100 text-green-800" :
                  company.subscription_type === "monthly" ? "bg-yellow-100 text-yellow-800" :
                  company.subscription_type === "pilot" ? "bg-orange-100 text-orange-800" :
                  company.subscription_type === "one-off" ? "bg-pink-100 text-pink-800" :
                  "bg-gray-100 text-gray-800"
                }`}>
                  {company.subscription_type === "half_yearly" ? "Half Yearly" :
                   company.subscription_type === "one-off" ? "One-Off" :
                   company.subscription_type.charAt(0).toUpperCase() + company.subscription_type.slice(1)}
                </span>
              )}
            </div>
          </div>
        </div>
        {!onArchiveToggle && (
          <div className="rounded-full bg-blue-50 p-2 transition-colors group-hover:bg-blue-100">
            <MdArrowForward className="h-6 w-6 text-blue-600 transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-2 text-gray-600">
            <MdBusiness className="h-5 w-5" />
            <span className="text-sm font-medium">Programs</span>
          </div>
          <p className="mt-1 text-2xl font-bold text-gray-900">{programCount}</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-2 text-gray-600">
            <MdPerson className="h-5 w-5" />
            <span className="text-sm font-medium">Users</span>
          </div>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {company.users?.length || 0}
          </p>
        </div>
      </div>

      {/* Program Dates - Side by Side */}
      <div className="mt-4">
        {(startTimeInfo || timeInfo) ? (
          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {/* Start Date */}
              {startTimeInfo && (
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5 mb-1">
                    <MdCalendarToday className="h-3.5 w-3.5 text-purple-600" />
                    <p className="text-xs font-medium text-gray-600">Starts</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    {moment(earliestProgramStartDate).format('MMM DD, YYYY')}
                  </p>
                  <span className={`inline-flex items-center self-start rounded-full px-2 py-0.5 text-xs font-semibold ${
                    startTimeInfo.isUpcoming
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-100 text-gray-700"
                  }`}>
                    {startTimeInfo.text}
                  </span>
                </div>
              )}

              {/* End Date */}
              {timeInfo && (
                <div className={`flex flex-col ${startTimeInfo ? 'lg:border-l lg:border-gray-200 lg:pl-3' : ''}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <MdCalendarToday className="h-3.5 w-3.5 text-blue-600" />
                    <p className="text-xs font-medium text-gray-600">Ends</p>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    {moment(latestProgramEndDate).format('MMM DD, YYYY')}
                  </p>
                  <span className={`inline-flex items-center self-start rounded-full px-2 py-0.5 text-xs font-semibold ${
                    timeInfo.isExpired
                      ? "bg-red-100 text-red-700"
                      : "bg-green-100 text-green-700"
                  }`}>
                    {timeInfo.text}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <MdCalendarToday className="h-4 w-4 text-gray-400" />
              <p className="text-xs text-gray-500">No programs created yet</p>
            </div>
          </div>
        )}
      </div>

      {/* Business Users */}
      {company.users && company.users.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            Team Members
          </p>
          <div className="flex flex-wrap gap-2">
            {company.users.slice(0, 3).map(user => {
              const isParent = user.role === 2 && !user.parent_user_id;
              const isApproved = user.is_approved !== false; // Default to true if undefined

              return (
                <div
                  key={user.user_id}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    !isApproved
                      ? "bg-red-500 text-white"
                      : isParent
                      ? "bg-blue-600 text-white"
                      : "bg-blue-50 text-blue-700"
                  }`}
                  title={
                    !isApproved
                      ? `${user.username} (Not Approved)`
                      : isParent
                      ? "Business Owner"
                      : "Team Member"
                  }
                >
                  {user.username}
                  {isParent && " 👑"}
                  {!isApproved && " ⚠️"}
                </div>
              );
            })}
            {company.users.length > 3 && (
              <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                +{company.users.length - 3} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    </div>
  );
};

export default CompanyCard;

